/**
 * API layer for translations feature.
 *
 * Handles HTTP communication with the admin-api-gateway
 * through the BFF proxy at /api/admin/*
 */

import { apiClient } from '@/infrastructure/api/client';
import { ApiTransportError } from '@/infrastructure/api/error';
import { assertAuthorizedOrClearSession } from '@/infrastructure/auth/unauthorized-session-bridge';
import { API_BASE_URL, DEFAULT_PLATFORM_ADMIN_LOCALE } from '@/shared/constants';
import type {
  TranslationsPage,
  TranslationDetail,
  Translation,
  TranslationFilters,
  CreateTranslationPayload,
  UpdateTranslationPayload,
  AddCommentPayload,
  UpdateKeyPayload,
  UpdateTagsPayload,
  UpdateDescriptionPayload,
  ImportPreviewResult,
  ConfirmImportPayload,
  ImportConfirmResult,
  MentionableUser,
  NotificationHistoryEntry,
} from './types';
import type {
  BulkDeleteI18nSkipReason,
  BulkForceReleaseI18nSkipReason,
} from '@microservices/dtos';

const BASE_PATH = '/v1.0/i18n/translations';

/**
 * The multipart uploads below bypass `apiClient` (it sends JSON), so they set
 * their own bound: a stalled upload must still end, so the caller's error state
 * runs and the spinner stops (PR 1399 review m10). The BFF proxy buffers the
 * whole body before it calls the gateway, and only that call is bound by its
 * 20 s (`DEFAULT_BACKEND_TIMEOUT_MS`, answered as a 504). This bound covers the
 * transfer to the BFF as well — a 5 MiB image on a slow link — so it is longer
 * than the client's REQUEST_TIMEOUT_MS.
 */
export const UPLOAD_TIMEOUT_MS = 120_000;

/**
 * POST a multipart body, time-bounded, with transport failures translated the
 * way `ApiClient` translates them (PR 1399 review 3 m10): the caller shows a
 * readable message instead of the raw "signal timed out" / "Failed to fetch",
 * and retry classifiers still see a transient error. The original rejection
 * stays as `cause`.
 */
async function postMultipart(url: string, body: FormData): Promise<Response> {
  try {
    return await fetch(url, {
      method: 'POST',
      body,
      credentials: 'include',
      // The locale hint apiClient sends on every request, so an upload's or a
      // preview's 400 comes back in the same language (PR 1399 review 7 m8).
      // No Content-Type: the browser sets the multipart boundary.
      headers: { 'X-Locale': DEFAULT_PLATFORM_ADMIN_LOCALE },
      signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'TimeoutError') {
      throw new ApiTransportError(
        'timeout',
        'Upload timed out. Please check your connection and try again.',
        error
      );
    }
    if (error instanceof TypeError) {
      throw new ApiTransportError(
        'network',
        'Network error. Please check your connection and try again.',
        error
      );
    }
    throw error;
  }
}

/**
 * Fetch paginated list of translations with filters.
 */
export async function fetchTranslations(
  filters: TranslationFilters = {}
): Promise<TranslationsPage> {
  const params: Record<string, string | number | boolean> = {};

  if (filters.page !== undefined) params.page = filters.page;
  if (filters.pageSize !== undefined) params.pageSize = filters.pageSize;
  if (filters.locale) params.locale = filters.locale;
  if (filters.domain) params.domain = filters.domain;
  if (filters.state) params.state = filters.state;
  if (filters.changeRequestState) params.changeRequestState = filters.changeRequestState;
  if (filters.keyPattern) params.keyPattern = filters.keyPattern;
  if (filters.search) params.search = filters.search;
  if (filters.keySearch) params.keySearch = filters.keySearch;
  if (filters.valueSearch) params.valueSearch = filters.valueSearch;
  if (filters.tags && filters.tags.length > 0) params.tags = filters.tags.join(',');
  if (filters.source) params.source = filters.source;
  if (filters.submittedByEmail) params.submittedByEmail = filters.submittedByEmail;
  if (filters.i18nKeyFormat) params.i18nKeyFormat = filters.i18nKeyFormat;
  if (filters.sortBy) params.sortBy = filters.sortBy;
  if (filters.sortOrder) params.sortOrder = filters.sortOrder;
  if (filters.includeDeleted !== undefined)
    params.includeDeleted = filters.includeDeleted;

  const response = await apiClient.get<{
    data: Translation[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }>(BASE_PATH, params);

  return {
    items: response.data.data,
    total: response.data.total,
    page: response.data.page,
    pageSize: response.data.limit,
    totalPages: response.data.totalPages,
  };
}

/**
 * Fetch a single translation by UUID with version history and related translations.
 */
export async function fetchTranslation(
  uuid: string,
  includeDeleted?: boolean
): Promise<TranslationDetail> {
  const params: Record<string, string | boolean> = {};
  if (includeDeleted !== undefined) params.includeDeleted = includeDeleted;

  const response = await apiClient.get<TranslationDetail>(
    `${BASE_PATH}/${uuid}`,
    Object.keys(params).length > 0 ? params : undefined
  );
  return response.data;
}

/**
 * Create a new translation.
 */
export async function createTranslation(
  payload: CreateTranslationPayload
): Promise<Translation> {
  const response = await apiClient.post<Translation>(BASE_PATH, payload);
  return response.data;
}

/**
 * Update an existing translation.
 */
export async function updateTranslation(
  uuid: string,
  payload: UpdateTranslationPayload
): Promise<Translation> {
  const response = await apiClient.put<Translation>(
    `${BASE_PATH}/${uuid}`,
    payload
  );
  return response.data;
}

/**
 * Delete a translation.
 */
export async function deleteTranslation(uuid: string): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/${uuid}`);
}

/**
 * Payload accepted by {@link bulkDeleteTranslations}.
 *
 * Server requires either an explicit list of UUIDs **or** a filter object
 * (mutually exclusive). Server enforces `state = Draft` regardless of input.
 */
export type BulkDeleteFilter = {
  locale?: string;
  domain?: string;
  source?: string;
  state?: string;
  keyPattern?: string;
  tags?: string[];
  search?: string;
};

/**
 * The backend's reason codes (`@microservices/dtos`, single source).
 * `modified_concurrently`: the row changed after the request
 * read it (another writer won the race), so it was not deleted; refresh to
 * see its state.
 */
export type BulkDeleteSkipReason = BulkDeleteI18nSkipReason;

export interface BulkDeleteResult {
  deleted: number;
  deletedUuids: string[];
  skipped: Array<{ uuid: string; reason: BulkDeleteSkipReason }>;
}

/**
 * Bulk-delete translations restricted to `state = Draft`.
 *
 * Pass either `uuids` or `filter` (server validates exclusivity). The result
 * `skipped[]` contains entries that were not deleted along with the reason
 * (most commonly `not_draft`).
 */
export async function bulkDeleteTranslations(payload: {
  uuids?: string[];
  filter?: BulkDeleteFilter;
}): Promise<BulkDeleteResult> {
  const response = await apiClient.post<BulkDeleteResult>(
    `${BASE_PATH}/bulk/delete`,
    payload,
  );
  return response.data;
}

/**
 * Add a comment to a translation.
 */
export async function addComment(
  uuid: string,
  payload: AddCommentPayload
): Promise<Translation> {
  const response = await apiClient.post<Translation>(
    `${BASE_PATH}/${uuid}/comments`,
    payload
  );
  return response.data;
}

/**
 * Upload a visual-context image to a translation's gallery.
 *
 * Multipart upload through the BFF admin proxy (same FormData approach as
 * {@link previewImport}; the proxy already forwards multipart bodies).
 * Server enforces 5 MiB / mime whitelist / max 10 images; the response
 * carries the translation uuid only.
 */
export async function uploadGalleryImage(
  uuid: string,
  file: File
): Promise<{ uuid: string }> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await postMultipart(`${API_BASE_URL}${BASE_PATH}/${uuid}/gallery`, formData);

  assertAuthorizedOrClearSession(response);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { message?: string }).message ??
        `Gallery image upload failed: ${response.status}`
    );
  }

  return response.json();
}

/**
 * Remove a visual-context image from a translation's gallery.
 * The stored binary is best-effort reaped server-side.
 */
export async function removeGalleryImage(
  uuid: string,
  fileUuid: string
): Promise<{ uuid: string }> {
  const response = await apiClient.delete<{ uuid: string }>(
    `${BASE_PATH}/${uuid}/gallery/${fileUuid}`
  );
  return response.data;
}

/**
 * Fetch the roster of users that can be @-mentioned in a comment on this
 * translation (i18n Experts + Reviewers + the translation's submitter).
 */
export async function fetchMentionableUsers(
  uuid: string
): Promise<MentionableUser[]> {
  const response = await apiClient.get<MentionableUser[]>(
    `${BASE_PATH}/${uuid}/mentionable-users`
  );
  return response.data;
}

/**
 * Fetch the notification-dispatch history for a translation.
 *
 * Each entry is one workflow event (state change, change request, comment,
 * bulk action, …) that dispatched a notification, with its recipients. The
 * admin-api-gateway forwards to the analytics microservice and returns them
 * newest-first. The audit is forward-looking, so an empty list is the normal
 * initial state for older translations.
 */
export async function fetchTranslationNotifications(
  uuid: string
): Promise<NotificationHistoryEntry[]> {
  const response = await apiClient.get<{ data: NotificationHistoryEntry[] }>(
    `${BASE_PATH}/${uuid}/notifications`
  );
  return response.data.data ?? [];
}

/**
 * Force-release a translation, bypassing the standard reviewer-approval flow.
 *
 * Requires the `i18n:force-release` permission. The optional `reason` is
 * recorded in the audit trail and surfaced to notifications.
 */
export async function forceReleaseTranslation(
  uuid: string,
  payload: { reason?: string } = {}
): Promise<Translation> {
  const response = await apiClient.post<Translation>(
    `${BASE_PATH}/${uuid}/force-release`,
    { uuid, ...payload }
  );
  return response.data;
}

/**
 * Reason codes for entries reported in {@link BulkForceReleaseResult.skipped}.
 *
 * - `already_released`: candidate is already in `Released` state.
 * - `deprecated`:        candidate is in `Deprecated` state.
 * - `not_found`:         no translation exists for the given UUID.
 * - `modified_concurrently`: the row changed after the request read it (another
 *                        writer won the race); refresh to see its state.
 * - `error`:             infrastructure / unexpected failure for that row.
 */
export type BulkForceReleaseSkipReason = BulkForceReleaseI18nSkipReason;

export interface BulkForceReleaseResult {
  released: number;
  releasedUuids: string[];
  skipped: Array<{ uuid: string; reason: BulkForceReleaseSkipReason }>;
}

/**
 * Bulk force-release translations, bypassing the standard review workflow.
 *
 * Selection-based only (v1): the caller passes an explicit list of UUIDs.
 * The required `reason` is recorded in the audit trail of every successfully
 * released row. Server enforces:
 *   - `i18n:force-release` permission;
 *   - skip rows already in `Released` or `Deprecated` (returned in `skipped[]`).
 */
export async function bulkForceReleaseTranslations(payload: {
  uuids: string[];
  reason: string;
}): Promise<BulkForceReleaseResult> {
  const response = await apiClient.post<BulkForceReleaseResult>(
    `${BASE_PATH}/bulk/force-release`,
    payload
  );
  return response.data;
}

/**
 * Update a translation's key.
 */
export async function updateTranslationKey(
  uuid: string,
  payload: UpdateKeyPayload
): Promise<Translation> {
  const response = await apiClient.patch<Translation>(
    `${BASE_PATH}/${uuid}/key`,
    payload
  );
  return response.data;
}

/**
 * Update a translation's tags.
 *
 * Tags are metadata and can be edited in any state; this never triggers a
 * workflow state transition. The backend replicates the tags to related
 * translations (same key, other locales).
 */
export async function updateTranslationTags(
  uuid: string,
  payload: UpdateTagsPayload
): Promise<Translation> {
  const response = await apiClient.patch<Translation>(
    `${BASE_PATH}/${uuid}/tags`,
    payload
  );
  return response.data;
}

/**
 * Update a translation's description.
 *
 * The description is context for translators and can be edited in any state;
 * this never triggers a workflow state transition. It is per locale, so it is
 * not replicated to related translations.
 */
export async function updateTranslationDescription(
  uuid: string,
  payload: UpdateDescriptionPayload
): Promise<Translation> {
  const response = await apiClient.patch<Translation>(
    `${BASE_PATH}/${uuid}/description`,
    payload
  );
  return response.data;
}

/**
 * Look up tags from existing translations by key.
 * Used during creation to pre-fill tags when a key already exists in other locales.
 */
export async function fetchTagsByKey(
  key: string
): Promise<{ tags: string[] }> {
  const response = await apiClient.get<{ tags: string[] }>(
    `${BASE_PATH}/lookup/tags-by-key`,
    { key }
  );
  return response.data;
}

/**
 * Search distinct tags across all translations (case-insensitive substring).
 * Used by the Tags autocomplete input.
 */
export async function searchTags(
  q?: string,
  limit = 10
): Promise<{ tags: string[] }> {
  const params: Record<string, string | number> = { limit };
  if (q && q.trim()) {
    params.q = q.trim();
  }
  const response = await apiClient.get<{ tags: string[] }>(
    `${BASE_PATH}/lookup/tags`,
    params
  );
  return response.data;
}

/**
 * Export translations as a downloadable file.
 */
export function getExportUrl(params: {
  format: 'excel' | 'csv' | 'figma-csv';
  sources?: string[];
  locale?: string;
  domain?: string;
  state?: string;
  changeRequestState?: string;
  keyPattern?: string;
  tags?: string[];
  search?: string;
  keySearch?: string;
  valueSearch?: string;
  submittedByEmail?: string;
  /**
   * Selection-scoped export. When provided, the server ignores all other
   * filters and exports every locale of the selected keys (key-expansion).
   */
  uuids?: string[];
}): string {
  const queryParams = new URLSearchParams();
  queryParams.set('format', params.format);
  // Selection scope is self-contained: the server ignores sources/filters when
  // `uuids` is present, so we omit them here to keep the URL minimal.
  if (params.uuids && params.uuids.length > 0) {
    queryParams.set('uuids', params.uuids.join(','));
    return `${API_BASE_URL}${BASE_PATH}/export?${queryParams.toString()}`;
  }
  if (params.sources && params.sources.length > 0) queryParams.set('sources', params.sources.join(','));
  if (params.locale) queryParams.set('locale', params.locale);
  if (params.domain) queryParams.set('domain', params.domain);
  if (params.state) queryParams.set('state', params.state);
  if (params.changeRequestState) queryParams.set('changeRequestState', params.changeRequestState);
  if (params.keyPattern) queryParams.set('keyPattern', params.keyPattern);
  if (params.tags && params.tags.length > 0) queryParams.set('tags', params.tags.join(','));
  if (params.search) queryParams.set('search', params.search);
  if (params.keySearch) queryParams.set('keySearch', params.keySearch);
  if (params.valueSearch) queryParams.set('valueSearch', params.valueSearch);
  if (params.submittedByEmail) queryParams.set('submittedByEmail', params.submittedByEmail);
  return `${API_BASE_URL}${BASE_PATH}/export?${queryParams.toString()}`;
}

/**
 * Upload a file for import preview.
 */
export async function previewImport(file: File): Promise<ImportPreviewResult> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await postMultipart(`${API_BASE_URL}${BASE_PATH}/import/preview`, formData);

  assertAuthorizedOrClearSession(response);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { message?: string }).message ?? `Import preview failed: ${response.status}`
    );
  }

  return response.json();
}

/**
 * Confirm an import with selected rows.
 */
export async function confirmImport(payload: ConfirmImportPayload): Promise<ImportConfirmResult> {
  const response = await apiClient.post<ImportConfirmResult>(
    `${BASE_PATH}/import/confirm`,
    payload
  );
  return response.data;
}

/**
 * Get import template download URL.
 */
export function getTemplateUrl(format: 'excel' | 'csv'): string {
  return `${API_BASE_URL}${BASE_PATH}/templates/${format}`;
}
